import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  LmsNotificationCallback,
  UpdateLmsNotificationCallbackRequest,
} from './live-notifications-types';

// ============================================
// Query keys
// ============================================

export const liveNotificationKeys = {
  all: ['livestream', 'notification_callbacks'] as const,
  list: (serviceId: string) =>
    ['livestream', 'notification_callbacks', serviceId] as const,
};

// ============================================
// Queries
// ============================================

export function useLmsNotificationCallbacks(serviceId: string) {
  return useQuery<LmsNotificationCallback[]>({
    queryKey: liveNotificationKeys.list(serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/callbacks`);
      return res.data?.data ?? res.data ?? [];
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useUpdateLmsNotificationCallback(
  serviceId: string,
  options?: UseMutationOptions<
    LmsNotificationCallback,
    Error,
    { id: string; req: UpdateLmsNotificationCallbackRequest }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      req,
    }: {
      id: string;
      req: UpdateLmsNotificationCallbackRequest;
    }) => {
      const res = await serviceApiV3.put(`${mediaBase(serviceId, 'streams')}/callbacks/${id}`, req);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveNotificationKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
